<template>
    <div
        class="relative p-5 rounded-xl flex flex-col gap-5 border border-border min-w-81.25 bg-surface-raised shadow-md fs-mask"
        :class="{
            'transition-opacity opacity-50 hover:opacity-100': isDismissed,
            'opacity-50': isInactive && !isDismissed,
            'hover:border-border-strong hover:-translate-y-0.75 hover:shadow-lg transition-[translate,colors,shadow]': !!onDetailsClick && !isDismissed,
        }">
        <div class="flex gap-2.5 items-start">
            <!-- When clickable, the button stretches across the whole card (::before overlay in
                 <style>) so a click anywhere navigates, while real buttons layer above it. -->
            <div class="flex gap-2.5 items-center grow min-w-0">
                <BaseAvatar v-if="name" size="md" theme="primary" class="shadow-[inset_0_0_0_1.75px_#0003]" aria-hidden="true" translate="no">
                    <span class="nadia-chat-bold">
                        {{ getFirstLastInitials(name) }}
                    </span>
                </BaseAvatar>
                <SkeletonLoader v-else-if="!name && showLoadingStates" class="rounded-full w-8 h-8"></SkeletonLoader>
                <div class="flex flex-col min-w-0">
                    <div class="flex gap-2.5">
                        <component
                            :is="onDetailsClick ? 'button' : 'div'"
                            class="flex items-center gap-2 min-w-0 text-left"
                            :class="{ stretched: !!onDetailsClick }"
                            @click="handleDetails">
                            <div class="flex items-center gap-2 min-w-0">
                                <p v-if="name" class="nadia-subtitle truncate">{{ name }}</p>
                                <p v-else-if="!name && !jobTitle && !showLoadingStates" class="nadia-subtitle text-valence-grey-400">(no name)</p>
                                <SkeletonLoader v-else-if="!name && showLoadingStates" class="w-40 my-1 h-[0.66lh] rounded-full"></SkeletonLoader>
                            </div>
                        </component>
                        <div class="flex items-center gap-2">
                            <BaseTooltip v-if="isHris && name" :as-child="false" trigger-aria-label="HRIS source indicator">
                                <template #trigger>
                                    <i class="relative bi bi-building text-ink-muted text-lg" aria-hidden="true"></i>
                                </template>
                                <template #content>Synced from HR</template>
                            </BaseTooltip>
                            <StatusChip v-if="isInactive && name" state="neutral" size="xs">Inactive</StatusChip>
                        </div>
                    </div>
                    <p v-if="jobTitle" class="body-small text-ink-muted truncate">{{ jobTitle }}</p>
                    <SkeletonLoader v-else-if="!jobTitle && showLoadingStates" class="w-40 my-1 h-[0.63lh] rounded-full"></SkeletonLoader>
                </div>
            </div>

            <div v-if="!shouldHideControls" class="relative shrink-0 flex items-center" @click.stop>
                <slot name="controls">
                    <i v-if="isDismissed" class="bi bi-plus-lg text-xl/none" title="Restore" aria-hidden="true" />
                    <BaseDropdownMenu v-else :sections="menuSections" content-class="z-50" @select="handleMenuSelect">
                        <template #trigger>
                            <button
                                type="button"
                                class="bi bi-three-dots text-xl/none text-valence-grey-500 hover:text-valence-grey-800 cursor-pointer leading-none"
                                aria-label="More options"
                                @click.stop />
                        </template>
                    </BaseDropdownMenu>
                </slot>
            </div>
        </div>
        <!-- Divider between the header and the card body, matching the People card layout. -->
        <div v-if="showBody" class="w-full border-t border-border" />
        <!-- Notes / summary text. Rendered as markdown so WorkIQ-generated notes (labeled markdown)
             display properly; plain user notes/summaries render unchanged. Truncated first to keep
             cards compact. -->
        <MarkdownText v-if="notes && !showLoadingStates" :text="truncate(notes, { length: notesTruncateLength })" class="body-small break-words" />
        <p v-else-if="notesPending && !showLoadingStates" class="body-small italic text-ink-muted">
            Nadia is writing up notes from your conversation — usually ready within a few hours.
        </p>
        <div v-else-if="showLoadingStates && !notes" class="flex flex-col gap-2">
            <SkeletonLoader class="w-full h-[0.63lh] rounded-full" />
            <SkeletonLoader class="w-full h-[0.63lh] rounded-full" />
            <SkeletonLoader class="w-3/5 h-[0.63lh] rounded-full" />
        </div>
        <!-- Action buttons row - moved to the bottom; only shown when connection prop is provided. -->
        <div v-if="connection && !isDismissed" class="relative flex flex-wrap gap-3 items-center justify-end mt-auto">
            <BaseTooltip v-if="hasPublicProfile && name" :as-child="false" trigger-aria-label="collaboration profile indicator" content-class="max-w-80" class="mr-auto">
                <template #trigger>
                    <span class="scaffold-details-small inline-flex items-center gap-2">
                        <img :src="CollabFilledIcon" alt="" aria-hidden="true" class="h-4 w-auto" />
                        Connected
                    </span>
                </template>
                <template #content>{{ collaborationProfileTooltipContent }}</template>
            </BaseTooltip>
            <BaseTooltip trigger-aria-label="chat button tooltip trigger" content-class="max-w-80">
                <template #trigger>
                    <BaseButton theme="neutral" size="sm" :disabled="isStartingChat" @click="handleStartChat"> Chat </BaseButton>
                </template>
                <template #content>{{ getChatMessage(name) }}</template>
            </BaseTooltip>
            <BaseTooltip trigger-aria-label="role play button tooltip trigger" content-class="max-w-80">
                <template #trigger>
                    <BaseButton theme="tertiary" size="sm" class="border border-border-strong flex items-center gap-1.5 flex-nowrap" @click="handleStartRolePlay">
                        <span class="whitespace-nowrap">Role Play</span>
                    </BaseButton>
                </template>
                <template #content>Practice a conversation with {{ name || "this person" }}</template>
            </BaseTooltip>
        </div>
        <div v-if="showWrongConnectionWarning" class="relative flex justify-end">
            <button v-if="!showDisambiguation" type="button" class="body-small text-right text-ink-muted cursor-pointer" @click="handleWrongConnection">Wrong Person?</button>
            <ChatConnectionDisambiguation v-else :initial-search-term="matchText" :show-cancel="true" @select="handleWrongPersonSelect" @cancel="showDisambiguation = false" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import CollabFilledIcon from "~img/collab_icons/collab-filled.svg";
import ChatConnectionDisambiguation from "~vue/ChatDetail/ChatConnectionDisambiguation.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useConnectionActions } from "~vue/composables/useConnectionActions";
import MarkdownText from "~vue/MarkdownText.vue";
import type { Connection, ConnectionDict } from "~vue/types/connection";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants.js";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { firstNameOf, getFirstLastInitials } from "~vue/utils/stringUtils";
import truncate from "lodash/truncate";
import { computed, ref } from "vue";

type ConnectionsCardProps = {
    connectionId: string;
    name?: string;
    jobTitle?: string;
    shouldHideControls?: boolean;
    notes?: string;
    notesPending?: boolean;
    showLoadingStates?: boolean;
    notesTruncateLength?: number;
    onDetailsClick?: (connectionId: string) => void;
    showWrongConnectionWarning?: boolean;
    matchText?: string;
    onWrongPersonSelect?: (selectedConnection: Connection) => void;
    isDismissed?: boolean;
    hasPublicProfile?: boolean;
    isHris?: boolean;
    isInactive?: boolean;
    connection?: ConnectionDict;
};

const {
    connectionId,
    name,
    jobTitle,
    notes,
    notesPending = false,
    shouldHideControls = false,
    onDetailsClick,
    hasPublicProfile,
    connection,
    onWrongPersonSelect,
    showLoadingStates = false,
    notesTruncateLength = 200,
    showWrongConnectionWarning = false,
    matchText = "",
    isDismissed = false,
    isHris = false,
    isInactive = false,
} = defineProps<ConnectionsCardProps>();

const emit = defineEmits<{
    delete: [connectionId: string];
    merge: [connectionId: string];
}>();

const { getChatMessage, startChat, startRolePlay } = useConnectionActions();

const showDisambiguation = ref(false);
const isStartingChat = ref(false);

// Whether to render the divider + body (notes/actions) below the header.
const showBody = computed(() => !isDismissed && (!!connection || !!notes || showLoadingStates));

const menuSections: DropdownMenuSection[] = [
    {
        id: "actions",
        items: [
            { id: "merge", type: "button", label: "Merge with another person", icon: "bi bi-front" },
            { id: "edit", type: "button", label: "Edit Details", icon: "bi bi-pencil" },
        ],
    },
    {
        id: "danger",
        items: [{ id: "remove", type: "button", label: "Remove from your people", icon: "bi bi-trash", theme: "danger" }],
    },
];

const handleDeleteClick = () => {
    emit("delete", connectionId);
};

const handleMergeClick = () => {
    emit("merge", connectionId);
};

const handleEditDetails = () => {
    router.visit(`/people/edit/${connectionId}/`);
};

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "merge") {
        handleMergeClick();
    } else if (item.id === "edit") {
        handleEditDetails();
    } else if (item.id === "remove") {
        handleDeleteClick();
    }
};

const navigateToDetails = () => {
    if (!onDetailsClick) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.CONNECTION_DETAIL_CLICKED, { connection_id: connectionId });
    onDetailsClick(connectionId);
};

const handleDetails = () => {
    navigateToDetails();
};

const handleWrongConnection = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_WRONG_CONNECTION_CLICKED, { connection_id: connectionId });
    showDisambiguation.value = true;
};

const handleWrongPersonSelect = (selectedConnection: Connection) => {
    onWrongPersonSelect?.(selectedConnection);
    showDisambiguation.value = false;
};

const handleStartChat = async () => {
    if (!connection || isStartingChat.value) {
        return;
    }
    isStartingChat.value = true;
    try {
        const redirectUrl = await startChat(connection, { chatSource: CHATSOURCE.CONNECTION_CARD });
        if (redirectUrl) {
            router.visit(redirectUrl);
        }
    } catch (e) {
        logError(`Failed to start chat: ${e instanceof Error ? e.message : JSON.stringify(e)}`);
    } finally {
        isStartingChat.value = false;
    }
};

const handleStartRolePlay = () => {
    if (!connection) {
        return;
    }
    startRolePlay(connection);
};

const collaborationProfileTooltipContent = computed(
    () =>
        `${name ? firstNameOf(name) : "This person"} has a linked Collaboration Profile, so Nadia has the context they chose to share about how they work best. Nadia sees only that — your conversations stay private.`,
);
</script>

<style scoped>
/* Stretched-link: the header navigation button expands to cover the whole card so a click
   anywhere navigates to the detail page. Interactive siblings (kebab, action buttons) opt
   back above it with `relative` to create a new stacking context. */
.stretched {
    cursor: pointer;
}

.stretched::before {
    content: "";
    position: absolute;
    inset: 0;
}

.popover :deep(.content) {
    border-radius: 24px;
}
</style>
